import { useState } from 'react';
import { App, Button, Flex, Input, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { FloatingWindow } from '@/shared/ui/FloatingWindow';
import { LinesGrid, useLinesGridApi } from '@/shared/ui/LinesGrid';
import { EditorFooter, useEditorWindow } from '@/shared/ui';
import type { LinesGridColumn } from '@/shared/ui/LinesGrid';
import { ImagePicker } from '@/shared/ui/ImagePicker';
import { photoFailureMessage, useCreateCategoryMutation, useUploadPendingImages } from '@/entities/category';
import { translateApiError } from '@/shared/api/errorToMessage';
import { useBreakpoint } from '@/shared/lib/hooks';

export interface RootGroupCreateWindowProps {
  onClose: () => void;
}

interface Row {
  key: string;
  name: string;
  /** Необязательное фото: уходит на сервер после создания группы, когда появится её id. */
  image: File | null;
}

let keySeq = 0;
function emptyRow(): Row {
  keySeq += 1;
  return { key: `grp${keySeq}-${Date.now()}`, name: '', image: null };
}

/** §2.1: добавление корневых групп категорий — та же табличная форма, что у
 *  `CategoryCreateWindow`/`CounterpartyCreateWindow`, вместо одиночной формы `CategoryWindow`:
 *  несколько групп за один заход, строка снизу добавляется автоматически по заполнению
 *  «Название», пустые строки не сохраняются. Группа не имеет `pcsPerBox` (DB CHECK
 *  `(parent_id IS NULL)=(pcs_per_box IS NULL)`), поэтому колонка N здесь не нужна. */
export function RootGroupCreateWindow({ onClose }: RootGroupCreateWindowProps) {
  const { message } = App.useApp();
  const { isMobile } = useBreakpoint();
  const [rows, setRows] = useState<Row[]>([emptyRow()]);
  const [createCategory, { isLoading }] = useCreateCategoryMutation();
  const uploadPendingImages = useUploadPendingImages();

  const { gridApiRef, api: gridApi } = useLinesGridApi();

  /** Пустая строка снизу и так ждёт ввода — новую не плодим, а встаём на неё. */
  function addRow(): boolean {
    const last = rows[rows.length - 1];
    if (last && !last.name && !last.image) {
      gridApi.focusCell(rows.length - 1, 0);
      return false;
    }
    setRows((prev) => [...prev, emptyRow()]);
    return true;
  }

  function removeRow(key: string) {
    setRows((prev) => (prev.length <= 1 ? prev : prev.filter((r) => r.key !== key)));
  }

  function updateName(key: string, rowIndex: number, value: string) {
    setRows((prev) => {
      const wasEmpty = !prev[rowIndex]?.name;
      const next = prev.map((r) => (r.key === key ? { ...r, name: value } : r));
      if (rowIndex === prev.length - 1 && wasEmpty && value.trim()) {
        return [...next, emptyRow()];
      }
      return next;
    });
  }

  function updateImage(key: string, image: File | null) {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, image } : r)));
  }

  const filled = rows.filter((r) => r.name.trim());

  async function handleSave() {
    if (filled.length === 0 || isLoading) return;
    try {
      const created = await Promise.all(filled.map((r) => createCategory({ name: r.name.trim() }).unwrap()));
      // Фото не обязательно и не должно мешать созданию: группы уже есть, неудачи — только предупреждение.
      const pending = filled.flatMap((r, i) => (r.image && created[i] ? [{ id: created[i].id, name: r.name.trim(), file: r.image }] : []));
      const failed = pending.length > 0 ? await uploadPendingImages(pending) : [];
      message.success(filled.length > 1 ? `Добавлено групп: ${filled.length}` : 'Группа добавлена');
      if (failed.length > 0) message.warning(photoFailureMessage(failed, 'группы'));
      onClose();
    } catch (e) {
      message.error(translateApiError(e));
    }
  }

  const columns: LinesGridColumn<Row>[] = [
    {
      key: 'name',
      title: 'Название',
      width: 260,
      render: (row, rowIndex) => (
        <Input
          className="nav-cell"
          data-nav-row={rowIndex}
          data-nav-col={0}
          value={row.name}
          autoFocus={rowIndex === 0}
          onChange={(e) => updateName(row.key, rowIndex, e.target.value)}
        />
      ),
    },
    {
      // Не `nav-cell`: выбор файла не входит в навигацию Enter/стрелками по сетке.
      key: 'photo',
      title: 'Фото',
      width: 80,
      render: (row) => (
        <ImagePicker
          file={row.image}
          size={24}
          title="Фото (необязательно)"
          onChange={(image) => updateImage(row.key, image)}
          onReject={(reason) => void message.warning(reason)}
        />
      ),
    },
  ];

  const editor = useEditorWindow({
    dirty: rows.some((r) => r.name.trim() || r.image),
    saveLabel: `Сохранить${filled.length > 1 ? ` (${filled.length})` : ''}`,
    saveBlockReason: filled.length === 0 ? 'Введите название хотя бы одной группы' : null,
    saving: isLoading,
    onSave: handleSave,
    onClose,
    gridApi,
  });

  return (
    <FloatingWindow
      kind="rootGroupCreate"
      title="Новые группы"
      onClose={editor.requestClose}
      hotkeys={editor.bindings}
      footer={<EditorFooter save={editor.save} saving={isLoading} onRequestClose={editor.requestClose} />}
    >
      {editor.dialog}
      <Flex vertical gap={8}>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Заполните название — строка снизу добавится автоматически. Пустые строки не сохраняются.
        </Typography.Text>
        {isMobile ? (
          // Телефон: вместо сетки — вертикальный список карточек-строк (название, фото, удалить).
          // Строка снизу, как и на десктопе, добавляется по заполнению «Название»; кнопка — для явного добавления.
          <Flex vertical gap={12}>
            {rows.map((row, rowIndex) => (
              <div key={row.key} className="m-card" style={{ marginTop: 0 }}>
                <Flex vertical gap={12}>
                  <Flex vertical gap={4}>
                    <Typography.Text>Название</Typography.Text>
                    <Input value={row.name} enterKeyHint="next" onChange={(e) => updateName(row.key, rowIndex, e.target.value)} />
                  </Flex>
                  <Flex justify="space-between" align="center" gap={8}>
                    <Flex align="center" gap={8} style={{ minWidth: 0 }}>
                      <ImagePicker
                        file={row.image}
                        size={64}
                        title="Фото (необязательно)"
                        onChange={(image) => updateImage(row.key, image)}
                        onReject={(reason) => void message.warning(reason)}
                      />
                      <Typography.Text type="secondary">Фото (необязательно)</Typography.Text>
                    </Flex>
                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      aria-label="Удалить строку"
                      disabled={rows.length <= 1}
                      onClick={() => removeRow(row.key)}
                      style={{ width: 44, height: 44, flex: '0 0 44px' }}
                    />
                  </Flex>
                </Flex>
              </div>
            ))}
            <Button size="large" icon={<PlusOutlined />} onClick={() => addRow()}>
              Добавить группу
            </Button>
          </Flex>
        ) : (
          <LinesGrid lines={rows} columns={columns} getRowKey={(r) => r.key} onAddRow={addRow} onRemoveRow={removeRow} gridApiRef={gridApiRef} removeRowAction={editor.removeRow} />
        )}
      </Flex>
    </FloatingWindow>
  );
}
